import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const bento2Schema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  cards: z
    .array(
      z.object({
        variant: z.enum(['ma', 'lime', 'dark', 'light']).default('ma'),
        tag: z.string().max(80).optional(),
        title: z.string().min(1).max(200),
        description: z.string().max(500),
        stats: z
          .array(
            z.object({
              value: z.string().max(40),
              label: z.string().max(80),
            }),
          )
          .max(4)
          .optional(),
        ctaLabel: z.string().max(80).optional(),
        ctaHref: z.string().max(500).optional(),
      }),
    )
    .min(1)
    .max(4),
});

export type Bento2Data = z.infer<typeof bento2Schema>;

export function Bento2Renderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  cards,
}: Bento2Data) {
  const safeCards = Array.isArray(cards) ? cards : [];
  if (safeCards.length === 0) return null;
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  const gridCls = `v5-bento-grid v5-bento-grid-${safeCards.length === 1 ? '1' : '2'}`;
  return (
    <section className="v5-section v5-bento-section">
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-bento-h2">{title}</h2>
          {subtitle ? <p className="v5-bento-sub">{subtitle}</p> : null}
        </div>
        <div className={gridCls}>
          {safeCards.map((c, i) => (
            <article key={i} className={`v5-bento-card v5-bento-${c.variant}`}>
              {c.tag ? (
                <div className="v5-bento-tag">
                  <span className="v5-bento-tag-dot"></span>
                  {c.tag}
                </div>
              ) : null}
              <h3 className="v5-bento-title">{c.title}</h3>
              <p className="v5-bento-desc">{c.description}</p>
              {c.stats && c.stats.length > 0 ? (
                <div className="v5-bento-mini-stats">
                  {c.stats.map((s, j) => (
                    <div key={j}>
                      <strong>{s.value}</strong>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {c.ctaLabel && c.ctaHref ? (
                <a href={c.ctaHref} className="v5-bento-cta">
                  {c.ctaLabel} →
                </a>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export const bento2Block: BlockDefinition<Bento2Data> = {
  meta: {
    type: 'bento-2',
    label: 'Bento 2 columnas (v5)',
    description: 'Grid bento con tarjetas grandes (tag, título, descripción, mini-stats y CTA).',
    category: 'content',
    icon: 'layout-grid',
  },
  schema: bento2Schema,
  defaults: {
    eyebrow: '● ¿En qué momento estás?',
    title: 'Cada operación es distinta. Empieza por aquí.',
    subtitle: 'Cuatro servicios principales bajo la misma metodología y el mismo equipo.',
    align: 'left',
    cards: [
      {
        variant: 'ma',
        tag: 'Vender empresa',
        title: 'Te ayudamos a vender en condiciones justas y confidenciales',
        description: 'Valoración previa, búsqueda discreta del comprador, negociación y acompañamiento legal. Honorarios a éxito.',
        stats: [
          { value: '+80', label: 'ventas cerradas' },
          { value: '92%', label: 'tasa éxito' },
          { value: '6,4m', label: 'plazo medio' },
        ],
        ctaLabel: 'Conoce el servicio Vender',
        ctaHref: '/vender-empresa',
      },
      {
        variant: 'lime',
        tag: 'Comprar empresa',
        title: 'Catálogo de oportunidades + mandatos de compra a medida',
        description: 'Acceso a +200 empresas en venta filtradas por sector, tamaño y ubicación. O mandatos personalizados.',
        stats: [
          { value: '+200', label: 'en cartera' },
          { value: '+40', label: 'mandatos' },
          { value: '+1.500', label: 'red' },
        ],
        ctaLabel: 'Conoce el servicio Comprar',
        ctaHref: '/comprar-empresa',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'cards',
      label: 'Tarjetas bento',
      type: 'array',
      max: 4,
      itemFields: [
        {
          name: 'variant',
          label: 'Variante color',
          type: 'select',
          options: [
            { label: 'M&A (azul)', value: 'ma' },
            { label: 'Lime (aceleradora)', value: 'lime' },
            { label: 'Oscuro', value: 'dark' },
            { label: 'Claro', value: 'light' },
          ],
        },
        { name: 'tag', label: 'Tag arriba', type: 'text', max: 80 },
        { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
        { name: 'description', label: 'Descripción', type: 'textarea', required: true, max: 500 },
        {
          name: 'stats',
          label: 'Mini stats',
          type: 'array',
          max: 4,
          itemFields: [
            { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
            { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 80 },
          ],
        },
        { name: 'ctaLabel', label: 'Texto CTA', type: 'text', max: 80 },
        { name: 'ctaHref', label: 'URL CTA', type: 'url' },
      ],
    },
  ],
  Renderer: Bento2Renderer,
};
